// src/components/inputs/TimeInput.jsx

import React, { useState, useRef, useEffect } from "react";
import {
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    Box,
    Typography,
    Button,
    useTheme,
    alpha,
    Grid,
    Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

/**
 * Custom 24-Hour Time Picker Component - Compact & Enhanced
 */
const TimeInput = ({
    label,
    value = "",
    onChange,
    required = false,
    disabled = false,
    error = false,
    helperText = "",
    fullWidth = true,
    ...rest
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [anchorEl, setAnchorEl] = useState(null);
    const [tempHour, setTempHour] = useState("00");
    const [tempMinute, setTempMinute] = useState("00");
    const inputRef = useRef(null);
    const hourInputRef = useRef(null);
    const minuteInputRef = useRef(null);

    // Parse current value
    useEffect(() => {
        if (value) {
            const [h, m] = value.split(":");
            setTempHour(h || "00");
            setTempMinute(m || "00");
        }
    }, [value]);

    // Focus hour input when popover opens
    useEffect(() => {
        if (anchorEl && hourInputRef.current) {
            setTimeout(() => {
                hourInputRef.current?.focus();
                hourInputRef.current?.select();
            }, 100);
        }
    }, [anchorEl]);

    const focusMinute = () => {
        minuteInputRef.current?.focus();
        minuteInputRef.current?.select();
    };

    const handleOpenPicker = (event) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClosePicker = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        const h = tempHour.padStart(2, '0');
        const m = tempMinute.padStart(2, '0');
        const newValue = `${h}:${m}`;
        onChange({ target: { value: newValue } });
        handleClosePicker();
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):?([0-5]?[0-9])?$/;

        if (timeRegex.test(inputValue) || inputValue === '') {
            onChange(e);
        }
    };

    const handleInputBlur = (e) => {
        let inputValue = e.target.value;

        if (inputValue) {
            inputValue = inputValue.replace(/[^\d:]/g, '');
            const parts = inputValue.split(':');
            let hours = parts[0] ? parseInt(parts[0], 10) : 0;
            let minutes = parts[1] ? parseInt(parts[1], 10) : 0;

            hours = Math.min(23, Math.max(0, hours));
            minutes = Math.min(59, Math.max(0, minutes));

            const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

            if (formattedTime !== value) {
                onChange({ target: { value: formattedTime } });
            }
        }
    };

    const handleHourChange = (e) => {
        let val = e.target.value.replace(/[^\d]/g, "");

        if (val === "") {
            setTempHour("");
            return;
        }

        if (val.length <= 2) {
            const numVal = parseInt(val, 10);

            if (numVal >= 0 && numVal <= 23) {
                setTempHour(val);

                // عند اكتمال رقمين بالكتابة: انقل التركيز للدقائق
                if (val.length === 2 && e.nativeEvent?.inputType === "insertText") {
                    setTimeout(focusMinute, 0);
                }
            } else if (val.length === 2 && numVal > 23) {
                setTempHour(val[0]);
            }
        }
    };


    const handleMinuteChange = (e) => {
        let val = e.target.value.replace(/[^\d]/g, '');

        if (val === '') {
            setTempMinute('');
            return;
        }

        // السماح بإدخال رقمين
        if (val.length <= 2) {
            const numVal = parseInt(val, 10);
            if (numVal >= 0 && numVal <= 59) {
                setTempMinute(val);
            } else if (val.length === 2 && numVal > 59) {
                // إذا كان الرقم أكبر من 59، استخدم الرقم الأول فقط
                setTempMinute(val[0]);
            }
        }
    };

    const handleHourBlur = () => {
        if (tempHour === '') {
            setTempHour('00');
        } else {
            setTempHour(tempHour.padStart(2, '0'));
        }
    };

    const handleMinuteBlur = () => {
        if (tempMinute === '') {
            setTempMinute('00');
        } else {
            setTempMinute(tempMinute.padStart(2, '0'));
        }
    };

    const handleHourKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab' || (e.key === ':' && tempHour)) {
            if (e.key !== 'Tab') e.preventDefault();
            minuteInputRef.current?.focus();
            minuteInputRef.current?.select();
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const newHour = (parseInt(tempHour || '0', 10) + 1) % 24;
            setTempHour(newHour.toString().padStart(2, '0'));
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const newHour = (parseInt(tempHour || '0', 10) - 1 + 24) % 24;
            setTempHour(newHour.toString().padStart(2, '0'));
        }
    };

    const handleMinuteKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApply();
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const newMinute = (parseInt(tempMinute || '0', 10) + 1) % 60;
            setTempMinute(newMinute.toString().padStart(2, '0'));
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const newMinute = (parseInt(tempMinute || '0', 10) - 1 + 60) % 60;
            setTempMinute(newMinute.toString().padStart(2, '0'));
        }
    };

    const incrementHour = () => {
        const newHour = (parseInt(tempHour || '0', 10) + 1) % 24;
        setTempHour(newHour.toString().padStart(2, '0'));
    };

    const decrementHour = () => {
        const newHour = (parseInt(tempHour || '0', 10) - 1 + 24) % 24;
        setTempHour(newHour.toString().padStart(2, '0'));
    };

    const incrementMinute = () => {
        const newMinute = (parseInt(tempMinute || '0', 10) + 1) % 60;
        setTempMinute(newMinute.toString().padStart(2, '0'));
    };

    const decrementMinute = () => {
        const newMinute = (parseInt(tempMinute || '0', 10) - 1 + 60) % 60;
        setTempMinute(newMinute.toString().padStart(2, '0'));
    };

    const setQuickTime = (hour, minute) => {
        setTempHour(hour.toString().padStart(2, '0'));
        setTempMinute(minute.toString().padStart(2, '0'));
    };

    const open = Boolean(anchorEl);

    // أوقات سريعة مختصرة
    const quickTimes = {
        am: [
            [6, 0], [6, 30],
            [7, 0], [7, 30],
            [8, 0], [8, 30],
            [9, 0], [9, 30], [10, 0], [10, 30], [11, 0], [11, 30],
        ],
        pm: [
            [12, 0], [13, 0], [14, 0], [15, 0],
            [16, 0], [17, 0], [18, 0], [19, 0],
            [20, 0], [21, 0], [22, 0], [23, 0],
        ],
    };


    return (
        <>
            <TextField
                ref={inputRef}
                fullWidth={fullWidth}
                className="input"
                type="text"
                label={label}
                value={value}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onClick={handleOpenPicker}
                placeholder={isArabic ? "00:00" : "HH:MM"}
                required={required}
                disabled={disabled}
                error={error}
                helperText={helperText}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    readOnly: false,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                onClick={handleOpenPicker}
                                disabled={disabled}
                                size="small"
                                sx={{ mr: -0.5 }}
                            >
                                <AccessTimeIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    direction: isArabic ? "rtl" : "ltr",
                    "& .MuiInputLabel-root": {
                        left: isArabic ? "auto" : undefined,
                        right: isArabic ? "1.75rem" : undefined,
                        transformOrigin: isArabic ? "right" : "left",
                    },
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: isDark
                            ? alpha(theme.palette.common.white, 0.05)
                            : alpha(theme.palette.common.black, 0.02),
                        cursor: "pointer",
                        "& input": {
                            height: "1.4375em",
                            padding: "16.5px 14px",
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "1rem",
                            letterSpacing: "normal",
                        },
                        "&:hover": {
                            backgroundColor: isDark
                                ? alpha(theme.palette.common.white, 0.08)
                                : alpha(theme.palette.common.black, 0.04),
                        },
                        "&.Mui-focused": {
                            backgroundColor: "transparent",
                        },
                    },
                }}
                {...rest}
            />

            {/* Compact Time Picker Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePicker}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: isArabic ? "right" : "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: isArabic ? "right" : "left",
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: 280,
                        borderRadius: 2,
                        boxShadow: theme.shadows[8],
                    },
                }}
            >
                <Box>
                    {/* Direct Input with Arrows - Compact */}
                    <Grid container spacing={1} alignItems="center" justifyContent="center">
                        {/* Hour Input */}
                        <Grid item xs={5}>
                            <Box sx={{ textAlign: "center" }}>
                                <IconButton
                                    size="small"
                                    onClick={incrementHour}
                                    sx={{
                                        width: "100%",
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        },
                                    }}
                                >
                                    <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                    inputRef={hourInputRef}
                                    value={tempHour}
                                    onChange={handleHourChange}
                                    onBlur={handleHourBlur}
                                    onKeyDown={handleHourKeyDown}
                                    inputProps={{
                                        maxLength: 2,
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '1.8rem',
                                            fontWeight: 700,
                                            padding: '8px 4px',
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        my: 0.5,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            "& fieldset": {
                                                borderWidth: 2,
                                            },
                                            "&:hover fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={decrementHour}
                                    sx={{
                                        width: "100%",
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        },
                                    }}
                                >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontSize: "0.7rem" }}>
                                    {isArabic ? "ساعة" : "Hour"}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Separator */}
                        <Grid item xs={2}>
                            <Typography
                                variant="h3"
                                sx={{
                                    textAlign: "center",
                                    fontWeight: 700,
                                    color: "primary.main",
                                    mb: 3,
                                }}
                            >
                                :
                            </Typography>
                        </Grid>

                        {/* Minute Input */}
                        <Grid item xs={5}>
                            <Box sx={{ textAlign: "center" }}>
                                <IconButton
                                    size="small"
                                    onClick={incrementMinute}
                                    sx={{
                                        width: "100%",
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        },
                                    }}
                                >
                                    <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                    inputRef={minuteInputRef}
                                    value={tempMinute}
                                    onChange={handleMinuteChange}
                                    onBlur={handleMinuteBlur}
                                    onKeyDown={handleMinuteKeyDown}
                                    inputProps={{
                                        maxLength: 2,
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '1.8rem',
                                            fontWeight: 700,
                                            padding: '8px 4px',
                                        },
                                    }}
                                    sx={{
                                        width: "100%",
                                        my: 0.5,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            "& fieldset": {
                                                borderWidth: 2,
                                            },
                                            "&:hover fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={decrementMinute}
                                    sx={{
                                        width: "100%",
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        "&:hover": {
                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        },
                                    }}
                                >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontSize: "0.7rem" }}>
                                    {isArabic ? "دقيقة" : "Min"}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Quick Times - Compact */}
                    <Box sx={{ mt: 2, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.8, display: "block", fontSize: "0.7rem" }}>
                            {isArabic ? "أوقات سريعة" : "Quick"}
                        </Typography>

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem", mb: 0.5 }}>
                                {isArabic ? "صباحًا" : "AM"}
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.5 }}>
                                {quickTimes.am.map(([h, m]) => {
                                    const hh = h.toString().padStart(2, "0");
                                    const mm = m.toString().padStart(2, "0");
                                    const isSelected = tempHour === hh && tempMinute === mm;

                                    return (
                                        <Chip
                                            key={`am-${h}-${m}`}
                                            label={`${hh}:${mm}`}
                                            size="small"
                                            onClick={() => setQuickTime(h, m)}
                                            color={isSelected ? "primary" : "default"}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{ fontSize: "0.7rem", height: 24, width: '100%' }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem", mb: 0.5 }}>
                                {isArabic ? "مساءً" : "PM"}
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.5 }}>
                                {quickTimes.pm.map(([h, m]) => {
                                    const hh = h.toString().padStart(2, "0");
                                    const mm = m.toString().padStart(2, "0");
                                    const isSelected = tempHour === hh && tempMinute === mm;

                                    return (
                                        <Chip
                                            key={`pm-${h}-${m}`}
                                            label={`${hh}:${mm}`}
                                            size="small"
                                            onClick={() => setQuickTime(h, m)}
                                            color={isSelected ? "primary" : "default"}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{ fontSize: "0.7rem", height: 24, width: '100%' }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    {/* Action Buttons - Compact */}
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleClosePicker}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                textTransform: "none",
                            }}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleApply}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                textTransform: "none",
                            }}
                        >
                            {isArabic ? "تطبيق" : "Apply"}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default TimeInput;
